import { TFunction } from "i18next";
import moment from "moment";
import React, { ForwardedRef, useEffect, useState } from "react";
import {
  Dropdown,
  Form,
  OverlayTrigger,
  Spinner,
  Tooltip
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  BiAlarmSnooze,
  BiBarChartAlt2,
  BiBook,
  BiConversation,
  BiDotsVerticalRounded,
  BiLink,
  BiPencil,
  BiPlay,
  BiPlus,
  BiSolidBot,
  BiSolidChat,
  BiSolidConversation,
  BiSolidDuplicate,
  BiSolidMagicWand,
  BiSolidMessageDetail,
  BiSolidPalette,
  BiTrash
} from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  ChatbotRTKService,
  useDeactivateAiTypeMutation,
  useDeactivateMcBotTypeMutation,
  useDeleteScenarioMutation,
  useGetAllScenariosQuery,
  useGetAssistantDetailMutation,
  useKnowledgeBaseDuplicateMutation,
  useSetDefaultScenariosMutation
} from "../../../app/services/chatbot-rtk.services";
import { IGetAllScenariosResponse } from "../../../app/services/chatbot-rtk.type";
import { confirm } from "../../../components/common/confirmation/confirmation";
import { UsageBot } from "../../../components/common/Usage";
import ApiFailure from "../../../components/ui/ApiFailure";
import ApiLoading from "../../../components/ui/ApiLoading";
import ProBadge from "../../../components/ui/ProBadge";
import { AlertsType } from "../../../constants/alerts.constant";
import { MultiLanguageUrl } from "../../../constants/multiLanguage.constant";
import { BiLouise } from "../../../features/appearance/AppearanceSharedAi";
import { selectUserProfile } from "../../../features/auth/auth.slice";
import {
  botsUiState,
  handleInitializeEditWizard,
  handleResetWizard,
  handleSetLivePreviewTargetBot,
  handleToggleBotsIntegrationModal,
  handleToggleCloneShareModal,
  handleToggleWizardModal
} from "../../../features/bot/botSlice";
import InfoMessageAlert from "../../../features/bot/InfoMessageAlert";
import {
  AIKittyType,
  BotCode,
  McBotFaceType
} from "../../../features/bot/types";
import { setCurrentScenario } from "../../../features/scenario.slice";
import usePermission from "../../../hooks/usePermission";
import { BotCategoryEnum } from "../../../utils/BotCategoryEnum";
import { BotTypeEnum } from "../../../utils/BotTypeEnum";
import { SumUserTypeEnum } from "../../../utils/SumUserTypeEnum";

export const renderStatusBadge = (
  status: string,
  t: TFunction<"translation", undefined>
) => {
  const types: Record<string, React.ReactNode> = {
    waiting_scan: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-sky-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-sky-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key4")}
        </div>
      </div>
    ),
    ready: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-emerald-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-emerald-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key5")}
        </div>
      </div>
    ),
    failed: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key6")}
        </div>
      </div>
    ),
    updating_bot: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-amber-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-amber-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key7")}
        </div>
      </div>
    ),
    scan_in_progress: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-sky-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-sky-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key9")}
        </div>
      </div>
    ),
    disabled: (
      <div className="tw-w-max tw-h-4 tw-px-4 tw-py-0.5 tw-bg-red-100 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-ml-3">
        <div className="tw-text-center tw-text-red-800 tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
          {t("General/key13")}
        </div>
      </div>
    )
  };

  return types[status];
};

const ChatBotItem = ({
  data,
  source,
  type,
  onSourceMutation
}: {
  data: IGetAllScenariosResponse;
  source?: IGetAllScenariosResponse[];
  type: "widget" | "link";
  onSourceMutation: React.Dispatch<
    React.SetStateAction<IGetAllScenariosResponse[]>
  >;
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const permissions = usePermission();
  const { livePreviewTargetBot } = useAppSelector(botsUiState);
  const [loading, setLoading] = useState(false);

  const [actionType, setActionType] = useState<
    "edit" | "appearance" | "visibility"
  >();

  const [deleteScenarioMutation, deleteScenarioMutationStatus] =
    useDeleteScenarioMutation();

  const [getAssistantDetails, getAssistantDetailsStatus] =
    useGetAssistantDetailMutation();

  const [setDefaultScenarios, setDefaultScenariosStatus] =
    useSetDefaultScenariosMutation();

  const [deactivateAiType, deactivateAiTypeResults] =
    useDeactivateAiTypeMutation();

  const [deactivateMcBotType, deactivateMcBotTypeResults] =
    useDeactivateMcBotTypeMutation();

  const [knowledgeBaseDuplicateRequest] = useKnowledgeBaseDuplicateMutation();

  const types: { [key: string]: string } = {
    MCBOT_01: t("DashboardCreatePartial/key3"),
    VIDEO_AI: t("DashboardCreatePartial/key7"),
    KITTY_02: t("DashboardCreatePartial/key4"),
    KITTY_03: t("DashboardCreatePartial/key2"),
    CLONE_01: t("DashboardCreatePartial/key1"),
    KITTY_01: t("DashboardCreatePartial/key9"),
    VIDEO_SCENARIO: t("DashboardCreatePartial/key5"),
    KITTY_04: t("DashboardCreatePartial/key6"),
    PHONE_HOTLINE: t("DashboardCreatePartial/key8")
  };

  const renderVideoFloatingButton = (icon: { type: string; value: string }) => {
    const icons: Record<string, React.ReactNode> = {
      "4": <BiLouise />,
      "1": <BiSolidBot size={30} />,
      "2": <BiSolidChat size={30} />,
      "3": <BiSolidMessageDetail size={30} />
    };

    if (icon.type === "icon")
      return (
        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-blue-500 tw-text-white">
          {icons[icon.value]}
        </div>
      );
    if (icon.type === "image")
      return (
        <img
          src={icon.value}
          alt={""}
          className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover"
        />
      );
    if (icon.type === "video")
      return (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover"
        >
          <source src={icon.value}></source>
        </video>
      );

    return null;
  };

  const handleRemoveChatbot = (data: IGetAllScenariosResponse) => {
    confirm(`${t(AlertsType.ISKBKO001)} "${data.title}"?`).then((res) => {
      if (res) {
        deleteScenarioMutation(data.id)
          .unwrap()
          .then(() => {
            onSourceMutation((prev) =>
              prev.filter((item) => item.id !== data.id)
            );

            dispatch(
              ChatbotRTKService.util.invalidateTags([
                "GetAllScenarios",
                "Package"
              ])
            );
          })
          .catch((err) => {
            toast.error(err.data.message || err.message);
          });
      }
    });
  };

  const handleVisibilityTimes = (data: IGetAllScenariosResponse) => {
    navigate(
      `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
        data.id
      }&tab=operating-hours`
    );
  };

  const handleDuplicateBot = (data: IGetAllScenariosResponse) => {
    confirm(
      t("Wizard/EditOptions/DuplicateConfirm", { botTitle: data.title })
    ).then((res) => {
      if (res) {
        knowledgeBaseDuplicateRequest(data.id)
          .then((res) => {
            t("Wizard/EditOptions/DuplicateSuccessMsg");
          })
          .catch((err) => {
            toast.error(err.data.message || err.message);
          });
      }
    });
  };

  const handleEditChatbot = (data: IGetAllScenariosResponse) => {
    navigate(
      `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
        data.id
      }&tab=config`
    );
  };

  const handleEditScenarios = (data: IGetAllScenariosResponse) => {
    getAssistantDetails(data.id)
      .unwrap()
      .then((value) => {
        dispatch(
          handleInitializeEditWizard({
            title: value.title,
            showModal: false,
            id: value.id,
            bot_code: value.type as BotCode,
            bot_type:
              (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO ||
              (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                : BotCategoryEnum.AI_BASED_CHATBOT,
            bot_config:
              (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO ||
              (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                ? ({
                    bot_code: value.type,
                    title: data.title,
                    template_id: 1
                  } as McBotFaceType)
                : ({ ...value.config } as AIKittyType)
          })
        );

        dispatch(setCurrentScenario(data.id));

        return navigate(
          `editor?type=${
            data.type === BotTypeEnum.TEXT_SCENARIO
              ? "text-scenario"
              : "video-scenario"
          }`
        );
      })
      .catch((err) => {
        toast.error(err.data.message || err.message);
      });
  };

  const handleChatbotActivation = (data: IGetAllScenariosResponse) => {
    if (data.is_default) {
      if (
        data.type === BotTypeEnum.VIDEO_SCENARIO ||
        data.type === BotTypeEnum.TEXT_SCENARIO
      ) {
        deactivateMcBotType(data.id)
          .unwrap()
          .then(() => {
            toast.success(t("BillingPage/btn-05"));
          })
          .catch((err) => {
            toast.error(err.data.message || err.message);
          });
      } else {
        deactivateAiType(data.id)
          .unwrap()
          .then(() => {
            toast.success(t("BillingPage/btn-05"));
          })
          .catch((err) => {
            toast.error(err.data.message || err.message);
          });
      }
    } else {
      setDefaultScenarios(data.id)
        .unwrap()
        .then(() => {
          toast.success(t("BillingPage/btn-04"));
        })
        .catch((err) => {
          if (err?.status == 401) {
            toast.warning(err.data.message || err.message);
          } else {
            toast.error(err.data.message || err.message);
          }
        });
    }
  };

  const handleIntegrateChatbot = (data: IGetAllScenariosResponse) => {
    dispatch(
      type === "widget"
        ? handleToggleBotsIntegrationModal({
            status: true,
            extra: {
              botId: data.id,
              botCode: data.type as BotCode,
              title: data.title
            }
          })
        : handleToggleCloneShareModal({
            status: true,
            data: { cloneId: data.id, logo: data.logo }
          })
    );
  };

  const handleEditAppearance = (data: IGetAllScenariosResponse) => {
    navigate(
      `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
        data.id
      }&tab=appearance`
    );
  };

  const handleConversationHistory = (data: IGetAllScenariosResponse) => {
    navigate(
      `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
        data.id
      }&tab=history-message`
    );
  };

  const handleEditStatistic = (data: IGetAllScenariosResponse) => {
    navigate(
      `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
        data.id
      }&tab=statistics`
    );
  };

  const handleEditKnowledgeBase = (data: IGetAllScenariosResponse) => {
    navigate(
      `/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${
        data.id
      }&tab=knowledgeBases`
    );
  };

  const handleManageLivePreview = (id: string) => {
    setLoading(true);
    if (livePreviewTargetBot) {
      dispatch(handleSetLivePreviewTargetBot(""));

      setTimeout(() => {
        dispatch(handleSetLivePreviewTargetBot(id));
      }, 2000);
    } else {
      dispatch(handleSetLivePreviewTargetBot(id));
    }
    setTimeout(() => {
      setLoading(false);
    }, 5000);
  };

  const usageStats = (data: IGetAllScenariosResponse) => {
    const isFreeUser = !permissions.extensionPage.main.includes(
      BotTypeEnum.HUMAN_CLONE
    );
    if (isFreeUser) {
      return <></>;
    } else {
      if (
        permissions.settingsPage.widget.customization.sessionLimit &&
        data.sessionLimit !== -1
      ) {
        return (
          <UsageBot
            use={data.sessionUsage}
            total={data.sessionLimit as number}
          />
        );
      } else {
        return (
          <div className="tw-w-max  tw-px-4 tw-py-0.5 tw-bg-gray-400 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex tw-h-[1rem] ">
            <div className="tw-text-center tw-text-white tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
              {data.sessionUsage} <BiSolidConversation />
            </div>
          </div>
        );
      }
    }
  };

  const botDependencies = (item: IGetAllScenariosResponse) => {
    if (item?.dependencies && item?.dependencies?.length > 0) {
      return (
        <Dropdown className="tw-h-[28px]">
          <Dropdown.Toggle as={CustomBotDependency}></Dropdown.Toggle>
          <Dropdown.Menu>
            <div className="tw-gap-2 tw-ml-2 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              <small>{t("Dependencies/message")}</small>
            </div>
            <div className="mt-3">
              {item?.dependencies.map((id) => {
                const temp = source?.find((dr) => dr.id === id);
                if (!temp) return null;

                return (
                  <Dropdown.Item key={id} disabled>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div className="tw-relative">
                        {temp.type === BotTypeEnum.VIDEO_SCENARIO ? (
                          renderVideoFloatingButton(temp.icon)
                        ) : (
                          <img
                            className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                            src={
                              temp.logo && temp.logo !== ""
                                ? temp.logo
                                : "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                            }
                            alt="chatbot message"
                          />
                        )}

                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              {t("General/key3")}{" "}
                              {!temp.is_default
                                ? t("DashboardHeaderSection/deactivated")
                                : t("DashboardHeaderSection/activated")}
                            </Tooltip>
                          }
                        >
                          <div
                            className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                              !temp.is_default
                                ? "tw-bg-red-400"
                                : "tw-bg-emerald-400 blink"
                            } tw-rounded-full  tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                          />
                        </OverlayTrigger>
                      </div>
                      <div className="tw-text-left">
                        <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {temp.title}
                        </div>
                        <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {types[temp.type]}
                        </div>
                      </div>
                    </div>
                  </Dropdown.Item>
                );
              })}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      );
    } else {
      return <></>;
    }
  };

  return (
    <div
      key={data.id}
      className="tw-self-stretch tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-start tw-items-center lg:tw-grid lg:tw-grid-cols-4 tw-inline-flex  tw-flex-col"
    >
      <div className="lg:tw-basis-2/5 lg:tw-order-1 tw-order-2 tw-px-6 tw-py-4 tw-flex tw-justify-between tw-items-center  lg:tw-w-auto tw-w-full">
        <div className="tw-justify-start tw-items-center tw-gap-[13px] tw-flex">
          <div className="tw-relative">
            {data.type === BotTypeEnum.VIDEO_SCENARIO ? (
              renderVideoFloatingButton(data.icon)
            ) : (
              <img
                className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                src={
                  data.logo && data.logo != ""
                    ? data.logo
                    : "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                }
                alt="chatbot logo"
              />
            )}

            <OverlayTrigger
              overlay={
                <Tooltip>
                  {t("General/key3")}{" "}
                  {!data.is_default
                    ? t("DashboardHeaderSection/deactivated")
                    : t("DashboardHeaderSection/activated")}
                </Tooltip>
              }
            >
              <div
                className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                  !data.is_default ? "tw-bg-red-400" : "tw-bg-emerald-400 blink"
                } tw-rounded-full  tw-shadow tw-border-2 tw-border-solid tw-border-white`}
              />
            </OverlayTrigger>
          </div>
          <div className="tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-inline-flex tw-w-full">
            <div className="tw-justify-start tw-items-start tw-gap-2 tw-inline-flex  tw-w-full tw-grid md:tw-block">
              <div className="tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight  tw-truncate">
                {data?.title.length > 15
                  ? data?.title.substring(0, 15) + "..."
                  : data?.title}

                {/* StatusBadges */}
                <div className="lg:tw-inline-flex tw-hidden">
                  {renderStatusBadge(data.status, t)}
                </div>
              </div>
            </div>
            <div className="tw-mt-2 tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none lg:tw-inline-flex  tw-items-center tw-gap-3">
              {moment(data.creation_date_time).format("ll")}
              {!(permissions.homePage.canCreate as Record<string, any>)[
                data.type as string
              ].upgrade && data.status === "disabled" ? (
                <ProBadge />
              ) : data.status !== "disabled" ? (
                <div className="tw-flex tw-flex-row tw-gap-3 tw-items-center">
                  <Form.Group className="tw-flex tw-items-center tw-gap-2 ">
                    {!setDefaultScenariosStatus.isLoading &&
                      !deactivateAiTypeResults.isLoading &&
                      !deactivateMcBotTypeResults.isLoading && (
                        <Form.Check
                          checked={data.is_default}
                          disabled={
                            deactivateAiTypeResults.isLoading ||
                            deactivateMcBotTypeResults.isLoading ||
                            (data.type ===
                              BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY &&
                              data.status !== "ready")
                          }
                          type="switch"
                          onChange={(e) => {
                            handleChatbotActivation(data);
                          }}
                          className="form-switch-small tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                        />
                      )}

                    {(setDefaultScenariosStatus.isLoading ||
                      deactivateAiTypeResults.isLoading ||
                      deactivateMcBotTypeResults.isLoading) && (
                      <Spinner size="sm" className="tw-mr-2" />
                    )}
                  </Form.Group>
                </div>
              ) : null}
            </div>
            <div className="twm-md-mt-0 tw-mt-2 tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none tw-inline-flex lg:tw-hidden">
              {types[data.type]}
            </div>
          </div>
        </div>
        <div className="tw-block lg:tw-hidden">
          {[
            BotTypeEnum.TEXT_SCENARIO.toString(),
            BotTypeEnum.AI_READY_TO_USE.toString(),
            BotTypeEnum.AI_WITH_KNOWLEDGE_BASE.toString(),
            BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY.toString(),
            BotTypeEnum.VIDEO_SCENARIO.toString()
          ].includes(data.type) &&
            data.status === "ready" && (
              <button
                className="tw-w-[110px] tw-border-none tw-h-8 tw-pl-2.5 tw-pr-3 tw-py-1.5 tw-bg-white tw-rounded-full tw-shadow tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-flex"
                onClick={() => {
                  handleManageLivePreview(data.id);
                }}
                disabled={
                  data.type === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY &&
                  data.status !== "ready"
                }
              >
                {loading ? (
                  <div className="tw-pl-1 tw-font-normal">
                    <Spinner size="sm" className="tw-mr-2" />
                  </div>
                ) : (
                  <BiPlay className="tw-w-4 tw-h-4 tw-relative" />
                )}

                <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("KnowledgeBasesCreateAssistantBot/btn-05")}
                </div>
              </button>
            )}
        </div>
      </div>
      <div className="lg:tw-basis-1/5 lg:tw-order-2 tw-order-2 tw-px-6 tw-py-4 tw-flex-col tw-justify-center tw-items-start lg:tw-inline-flex tw-hidden lg:tw-w-auto tw-w-full">
        <div className="tw-self-stretch tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          {types[data.type]}
        </div>
        <div className="tw-self-stretch tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
          {[BotTypeEnum.TEXT_SCENARIO, BotTypeEnum.VIDEO_SCENARIO].includes(
            data.type as BotTypeEnum
          )
            ? t("IntegrationsPage/scenario")
            : t("IntegrationsPage/ai")}
        </div>
      </div>
      <div className="lg:tw-basis-2/5 lg:tw-order-3 tw-order-1 tw-grow tw-shrink tw-basis-0 tw-px-6 tw-py-4 lg:tw-justify-start tw-items-center tw-gap-2 tw-flex lg:tw-flex-row tw-flex-row tw-justify-between lg:tw-w-auto tw-w-full ">
        {usageStats(data)}
        {botDependencies(data)}
        <div className="lg:tw-hidden tw-block">
          {renderStatusBadge(data.status, t)}
        </div>
      </div>
      <div className="lg:tw-basis-2/5 tw-order-4 tw-grow tw-shrink tw-basis-0 tw-px-6 tw-py-4 tw-justify-end tw-items-center tw-gap-2 tw-flex lg:tw-flex-row tw-flex-col lg:tw-w-auto tw-w-full">
        <div className="lg:tw-justify-end tw-justify-between tw-items-center tw-gap-3 tw-flex lg:tw-w-auto tw-w-full">
          <div className="tw-flex tw-gap-2 tw-items-center">
            {data?.saveStats && (
              <div>
                <BiBarChartAlt2 className="blink" />
              </div>
            )}

            {data?.saveConversation && (
              <div>
                <BiConversation className="blink" />
              </div>
            )}
            {data?.operatingHours && (
              <div>
                <BiAlarmSnooze />
              </div>
            )}
          </div>

          <div className="lg:tw-block tw-hidden">
            {[
              BotTypeEnum.TEXT_SCENARIO,
              BotTypeEnum.AI_READY_TO_USE,
              BotTypeEnum.AI_WITH_KNOWLEDGE_BASE,
              BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY,
              BotTypeEnum.VIDEO_SCENARIO
            ].includes(data.type as BotTypeEnum) &&
              data.status === "ready" && (
                <button
                  className="lg:tw-w-[120px] tw-w-auto tw-border-none tw-h-8 tw-pl-2.5 tw-pr-3 tw-py-1.5 tw-bg-white tw-rounded-full tw-shadow tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-flex"
                  onClick={() => {
                    handleManageLivePreview(data.id);
                  }}
                  disabled={
                    data.type === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY &&
                    data.status !== "ready"
                  }
                >
                  {loading ? (
                    <div className="tw-pl-1 tw-font-normal">
                      <Spinner size="sm" className="tw-mr-2" />
                    </div>
                  ) : (
                    <BiPlay className="tw-w-4 tw-h-4 tw-relative" />
                  )}

                  <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("KnowledgeBasesCreateAssistantBot/btn-05")}
                  </div>
                </button>
              )}
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-4 tw-w-full lg:tw-w-auto">
          <button
            className={`tw-px-[15px] tw-py-[7px] tw-border-0 ${"tw-bg-primary"}  tw-rounded-[17px] tw-shadow tw-justify-center tw-items-center tw-flex tw-text-white tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight lg:tw-w-auto tw-w-full`}
            onClick={() => handleIntegrateChatbot(data)}
          >
            {data.type === BotTypeEnum.HUMAN_CLONE
              ? t("LatestHumanClones/key4")
              : t("Wizard/general/key8")}
          </button>

          {deleteScenarioMutationStatus.isLoading && (
            <Spinner size="sm" className="tw-mr-2" />
          )}

          {!deleteScenarioMutationStatus?.isLoading && (
            <Dropdown>
              <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    setActionType("edit");
                    handleEditChatbot(data);
                  }}
                  eventKey="4"
                >
                  <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                    <BiPencil className="tw-w-5 tw-h-5 tw-relative" />
                    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("Wizard/EditOptions/EditBotProfile")}
                    </div>
                  </div>
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="3"
                  onClick={() => {
                    setActionType("appearance");
                    handleEditAppearance(data);
                  }}
                >
                  <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                    <BiSolidPalette className="tw-w-5 tw-h-5 tw-relative" />
                    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("Wizard/EditOptions/Appearance")}
                    </div>
                  </div>
                </Dropdown.Item>

                {[
                  BotTypeEnum.TEXT_SCENARIO,
                  BotTypeEnum.VIDEO_SCENARIO
                ].includes(data.type as BotTypeEnum) && (
                  <Dropdown.Item
                    disabled={getAssistantDetailsStatus.isLoading}
                    onClick={() => {
                      setActionType("edit");
                      handleEditScenarios(data);
                    }}
                  >
                    <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                      <BiSolidMagicWand className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("Wizard/EditOptions/EditScenario")}
                      </div>
                    </div>
                  </Dropdown.Item>
                )}

                {(data.type === BotTypeEnum.HUMAN_CLONE ||
                  data.type === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE) && (
                  <Dropdown.Item
                    onClick={() => {
                      setActionType("edit");
                      handleEditKnowledgeBase(data);
                    }}
                    eventKey="4"
                  >
                    <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                      <BiBook className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("Wizard/EditOptions/KnowledgeBase")}
                      </div>
                    </div>
                  </Dropdown.Item>
                )}

                {(data.type === "KITTY_01" ||
                  data.type === BotTypeEnum.AI_READY_TO_USE ||
                  data.type === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
                  data.type === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY) && (
                  <Dropdown.Item
                    onClick={() => {
                      handleConversationHistory(data);
                    }}
                    eventKey="1"
                  >
                    <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                      <BiConversation className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("Wizard/EditOptions/ConversationHistory")}
                      </div>
                    </div>
                  </Dropdown.Item>
                )}

                {data.type != BotTypeEnum.HUMAN_CLONE && (
                  <Dropdown.Item
                    onClick={() => {
                      handleEditStatistic(data);
                    }}
                    eventKey="1"
                  >
                    <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                      <BiBarChartAlt2 className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("Wizard/EditOptions/Statistics")}
                      </div>
                    </div>
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  onClick={() => {
                    setActionType("visibility");
                    handleVisibilityTimes(data);
                  }}
                  eventKey="2"
                >
                  <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                    <BiAlarmSnooze className="tw-w-5 tw-h-5 tw-relative" />
                    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t("Wizard/EditOptions/OperatingHours")}
                    </div>
                  </div>
                </Dropdown.Item>

                {[
                  BotTypeEnum.TEXT_SCENARIO,
                  BotTypeEnum.VIDEO_SCENARIO
                ].includes(data.type as BotTypeEnum) && (
                  <Dropdown.Item
                    onClick={() => {
                      handleDuplicateBot(data);
                    }}
                  >
                    <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                      <BiSolidDuplicate className="tw-w-5 tw-h-5 tw-relative" />
                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t("Wizard/EditOptions/Duplicate")}
                      </div>
                    </div>
                  </Dropdown.Item>
                )}

                <Dropdown.Item
                  onClick={() => handleRemoveChatbot(data)}
                  disabled={deleteScenarioMutationStatus.isLoading}
                  eventKey="5"
                >
                  <div className="tw-h-9  tw-justify-start tw-items-center tw-gap-3 tw-inline-flex ">
                    <BiTrash className="tw-w-5 tw-h-5 tw-relative tw-text-red-500" />
                    <div className="tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-text-red-500">
                      {t("Wizard/EditOptions/DeleteChatbot")}
                    </div>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>
    </div>
  );
};

const KnowledgeBasesPage = ({ type }: { type: "link" | "widget" }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState(null);
  const profile = useAppSelector(selectUserProfile);

  const permission = usePermission();

  const { isLoading, isError, isSuccess, refetch, data, isFetching } =
    useGetAllScenariosQuery({ botType: type });

  const [filterableDat, setFilterableData] = useState<
    IGetAllScenariosResponse[]
  >([]);

  const handleAddNewChatbot = () => {
    dispatch(handleResetWizard());
    if (
      (type === "link" && permission?.extensionPage.upgrade) ||
      (type === "widget" && !permission.widgetPage.addNewBot)
    )
      return;

    dispatch(
      handleToggleWizardModal({
        status: true,
        category: type === "widget" ? "widget" : "clone"
      })
    );
  };

  const handleSearchChatbots = (value: string) => {
    if (value?.length === 0) return setFilterableData(data ?? []);

    const filteredData = data?.filter((item) =>
      item.title.toLowerCase().includes(value.toLowerCase())
    );

    setFilterableData(filteredData ?? []);
  };

  useEffect(() => {
    if (isSuccess) {
      setFilterableData(data ?? []);

      if (searchText) handleSearchChatbots(searchText);
    }
  }, [isSuccess, isFetching]);

  return (
    <section className="chatbots-page tw-py-4 tw-px-6">
      {profile?.user_type == SumUserTypeEnum.BUSINESSPARTNER && (
        <div className="tw-w-full tw-my-3">
          <InfoMessageAlert
            texts={[t("KnowledgeBasesPage/t-11")]}
            isClose={true}
          />
        </div>
      )}
      {/* Search bar */}
      <div className="tw-w-full tw-h-[38px] tw-px-[17px] tw-py-[9px] tw-bg-white tw-rounded-[89px] tw-border tw-border-gray-200 tw-justify-start tw-items-center tw-inline-flex tw-mb-4">
        <input
          type="search"
          placeholder={t("KnowledgeBasesPage/l-01")}
          className="focus-visible:tw-outline-none focus:tw-outline-none tw-grow tw-shrink tw-basis-0 tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Inter'] tw-leading-tight tw-border-0"
          onKeyUp={(e: any) => {
            handleSearchChatbots(e?.target?.value);
            setSearchText(e?.target?.value);
          }}
        />
      </div>

      {/* Data render */}
      <div className="tw-w-full tw-shadow tw-flex-col  tw-justify-start tw-items-start tw-gap-3 tw-inline-flex">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-flex">
          {/* Headers */}
          <div className="tw-self-stretch lg:tw-grid lg:tw-grid-cols-4  tw-justify-start tw-items-start tw-inline-flex tw-border-0 tw-border-solid tw-border-b tw-border-gray-200">
            <div className="tw-basis-2/5 tw-h-10 tw-px-7 tw-py-3 tw-bg-gray-50 tw-rounded-tl-lg tw-border-b tw-border-gray-200 tw-justify-start tw-items-center lg:tw-flex tw-hidden">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("KnowledgeBasesCreateAssistantBot/l-03")}
              </div>
            </div>
            <div className="tw-basis-1/5 tw-h-10 tw-px-7 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-justify-start tw-items-center lg:tw-flex tw-hidden">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                TYPE
              </div>
            </div>
            <div className="tw-basis-2/5 tw-h-10 tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-justify-start tw-items-center lg:tw-flex tw-hidden">
              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("KnowledgeBasesCreateAssistantBot/l-04")}
              </div>
            </div>

            <div className="tw-basis-2/5 tw-grow tw-shrink tw-h-10 tw-px-8 tw-py-3 tw-bg-gray-50 tw-rounded-tr-lg tw-border-b tw-border-gray-200 lg:tw-flex tw-hidden"></div>
          </div>

          {/* Add new chatbot action */}
          <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center  tw-inline-flex">
            <button
              className="tw-border-0  tw-gap-3 tw-self-stretch tw-pl-3 tw-pr-6 tw-py-2.5 tw-bg-white tw-rounded-lg tw-justify-center tw-items-center  tw-flex"
              onClick={handleAddNewChatbot}
              disabled={
                (type === "link" && permission?.extensionPage.upgrade) ||
                (type === "widget" && !permission.widgetPage.addNewBot)
              }
            >
              <div
                className={`tw-p-1 ${
                  (type === "link" && permission?.extensionPage.upgrade) ||
                  (type === "widget" && !permission.widgetPage.addNewBot)
                    ? "tw-bg-gray-400"
                    : "tw-bg-primary"
                } tw-rounded-[19px] tw-shadow tw-justify-center tw-items-center tw-flex`}
              >
                <div className="tw-w-5 tw-h-5 tw-relative tw-text-white tw-flex tw-flex-col tw-items-center tw-justify-center">
                  <BiPlus size={22} />
                </div>
              </div>
              <div
                className={`${
                  (type === "link" && permission?.extensionPage.upgrade) ||
                  (type === "widget" && !permission.widgetPage.addNewBot)
                    ? "tw-text-gray-400"
                    : "tw-text-primary"
                } tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight tw-flex tw-items-center tw-gap-2`}
              >
                {type === "widget"
                  ? t("LatestChatBots/key4")
                  : t("LatestHumanClones/key5")}
              </div>
            </button>

            {(type === "link" && permission?.extensionPage.upgrade) ||
            (type === "widget" && !permission.widgetPage.addNewBot) ? (
              <ProBadge />
            ) : (
              ""
            )}
          </div>

          {/* Mock table data */}

          {isLoading ? (
            <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
              <ApiLoading />
            </div>
          ) : isError ? (
            <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
              <ApiFailure refetch={refetch} />
            </div>
          ) : (
            <React.Fragment>
              {filterableDat.length > 0 && (
                <React.Fragment>
                  {filterableDat.map((item) => (
                    <ChatBotItem
                      key={item.id}
                      source={data}
                      data={item}
                      onSourceMutation={setFilterableData}
                      type={type}
                    />
                  ))}
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    </section>
  );
};

export default KnowledgeBasesPage;

interface CustomToggleProps {
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}

const CustomToggle = React.forwardRef<HTMLSpanElement, CustomToggleProps>(
  ({ children, onClick }, ref) => (
    <span
      ref={ref as ForwardedRef<HTMLSpanElement>}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
      <BiDotsVerticalRounded className="tw-cursor-pointer tw-w-5 tw-h-5" />
    </span>
  )
);

const CustomBotDependency = React.forwardRef<
  HTMLSpanElement,
  CustomToggleProps
>(({ children, onClick }, ref) => (
  <span
    ref={ref as ForwardedRef<HTMLSpanElement>}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <div className="tw-w-max  tw-px-4 tw-py-0.5 tw-bg-gray-400 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex  tw-h-[1rem]">
      <div className="tw-text-center tw-text-white tw-text-xs tw-font-medium tw-font-['Inter'] tw-leading-none">
        <BiLink className="tw-cursor-pointer tw-w-5 tw-h-5" />
      </div>
    </div>
  </span>
));
